import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Search, UserPlus, Edit, Trash2, Key, DollarSign, ShoppingBag,
  Mail, Building2, MapPin, ChevronUp, ChevronDown, Send, Download,
  AlertCircle, Shield, Activity, MessageSquare, Users, Ban, ShieldOff, Store,
} from 'lucide-react';

/**
 * CustomersListPanel — the main customers sub-tab (bulk toolbar + list + expanded detail).
 * Extracted from AdminPage.jsx on Apr 22, 2026.
 *
 * Parent owns all state + handlers; this is a controlled presentational component.
 */
const CustomersListPanel = ({
  filteredCustomers,
  customerSearch,
  setCustomerSearch,
  selectedCustomerIds,
  isBulkDeleting,
  isExporting,
  expandedCustomer,
  setExpandedCustomer,
  customerConsentForms,
  setCustomerConsentForms,
  pricingTiers,
  discountCodes,
  adminFetch,
  API_URL,
  // Handlers
  openCustomerModal,
  handleDeleteCustomer,
  handleBanCustomer,
  handleUnbanCustomer,
  toggleCustomerSelection,
  toggleSelectAllFiltered,
  handleAutoFlagSuspicious,
  handleBulkDeleteCustomers,
  handleExportCustomers,
  openNewsletterModal,
  openCashPaymentModal,
  openPreorderModal,
  setPasswordResetCustomer,
  setShowPasswordResetModal,
  fetchCustomers,
}) => {
  return (
                  <>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between mb-6">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search customers..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="pl-10"
                      data-testid="customer-search-input"
                    />
                  </div>
                  <Button 
                    onClick={() => openCustomerModal()}
                    className="bg-teal-600 hover:bg-teal-700 text-white sm:ml-4"
                    data-testid="add-customer-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Customer
                  </Button>
                </div>

                {/* Bulk management toolbar */}
                <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="customer-bulk-toolbar">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filteredCustomers.length > 0 && filteredCustomers.every(c => selectedCustomerIds.has(c.customer_id))}
                      onChange={toggleSelectAllFiltered}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                      data-testid="select-all-filtered-customers"
                    />
                    Select all ({filteredCustomers.length})
                  </label>
                  {selectedCustomerIds.size > 0 && (
                    <span className="text-sm text-slate-500" data-testid="selected-customer-count">
                      {selectedCustomerIds.size} selected
                    </span>
                  )}
                  <div className="flex-1" />
                  <Button
                    onClick={openNewsletterModal}
                    variant="outline"
                    size="sm"
                    className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                    data-testid="send-newsletter-btn"
                  >
                    <Send className="h-4 w-4 mr-1.5" />
                    Send Newsletter
                  </Button>
                  <Button
                    onClick={handleAutoFlagSuspicious}
                    variant="outline"
                    size="sm"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid="auto-flag-suspicious-btn"
                  >
                    <AlertCircle className="h-4 w-4 mr-1.5" />
                    Auto-flag Suspicious
                  </Button>
                  <Button
                    onClick={handleBulkDeleteCustomers}
                    disabled={selectedCustomerIds.size === 0 || isBulkDeleting}
                    variant="outline"
                    size="sm"
                    className="text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-40"
                    data-testid="bulk-delete-selected-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    {isBulkDeleting ? 'Deleting...' : `Delete Selected${selectedCustomerIds.size > 0 ? ` (${selectedCustomerIds.size})` : ''}`}
                  </Button>
                  <div className="relative group" data-testid="export-customers-group">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-teal-700 border-teal-300 hover:bg-teal-50"
                      disabled={isExporting}
                      data-testid="export-customers-btn"
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      {isExporting ? 'Exporting...' : 'Export CSV'}
                      <ChevronDown className="h-3 w-3 ml-1.5" />
                    </Button>
                    <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-20">
                      <button
                        onClick={() => handleExportCustomers('all')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 rounded-t-lg"
                        data-testid="export-all-customers"
                      >
                        <div className="font-medium">All customers</div>
                        <div className="text-xs text-slate-500">Every record in the database</div>
                      </button>
                      <button
                        onClick={() => handleExportCustomers('with_orders')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-t border-slate-100"
                        data-testid="export-paying-customers"
                      >
                        <div className="font-medium">Paying customers only</div>
                        <div className="text-xs text-slate-500">Customers with 1+ orders</div>
                      </button>
                      <button
                        onClick={() => handleExportCustomers('subscribed')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-t border-slate-100 rounded-b-lg"
                        data-testid="export-subscribed-customers"
                      >
                        <div className="font-medium">Newsletter subscribers</div>
                        <div className="text-xs text-slate-500">marketing_consent = true only</div>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {filteredCustomers.map((customer) => (
                    <Card key={customer.customer_id} className="border-slate-100">
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-4">
                            <input
                              type="checkbox"
                              checked={selectedCustomerIds.has(customer.customer_id)}
                              onChange={(e) => { e.stopPropagation(); toggleCustomerSelection(customer.customer_id); }}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                              data-testid={`customer-checkbox-${customer.customer_id}`}
                              aria-label={`Select customer ${customer.name || customer.email}`}
                            />
                            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                              <span className="text-teal-700 font-semibold">
                                {(customer.name || 'U')[0].toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">{customer.name || 'Unnamed'}</h3>
                                {customer.pricing_tier_id && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    {pricingTiers.find(t => t.tier_id === customer.pricing_tier_id)?.name || 'Special'}
                                  </Badge>
                                )}
                                <Badge className={`text-xs ${
                                  customer.status === 'banned' ? 'bg-red-100 text-red-700' :
                                  customer.status === 'vip' ? 'bg-amber-100 text-amber-700' :
                                  customer.status === 'active' ? 'bg-green-100 text-green-700' :
                                  'bg-slate-100 text-slate-500'
                                }`}>
                                  {customer.status || 'active'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {customer.email || 'No email'}
                                </span>
                                {customer.company && (
                                  <span className="flex items-center gap-1">
                                    <Building2 className="h-3 w-3" />
                                    {customer.company}
                                  </span>
                                )}
                                {customer.address && customer.address.city && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {customer.address.city}{customer.address.state && `, ${customer.address.state}`}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                {customer.allow_local_pickup && (
                                  <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full">Pickup</span>
                                )}
                                {customer.allow_cash_payment && (
                                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Cash</span>
                                )}
                                {customer.allow_consent_send && (
                                  <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">Consent Sender</span>
                                )}
                                {discountCodes.some(c => c.assigned_customer_email && c.assigned_customer_email.toLowerCase() === (customer.email || '').toLowerCase()) && (
                                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-testid="affiliate-badge">Affiliate</span>
                                )}
                                {customer.research_consent?.status === 'signed' ? (
                                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Consent Signed</span>
                                ) : customer.research_consent?.status === 'pending' ? (
                                  <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">Consent Pending</span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right text-sm">
                              <p className="text-slate-900 font-medium">{customer.total_orders || 0} orders</p>
                              <p className="text-slate-500">${(customer.total_spent || 0).toFixed(2)} spent</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  const newId = expandedCustomer === customer.customer_id ? null : customer.customer_id;
                                  setExpandedCustomer(newId);
                                  if (newId && customer.allow_consent_send && !customerConsentForms[newId]) {
                                    adminFetch(`${API_URL}/api/admin/customers/${newId}/consent-forms`)
                                      .then(r => r.json())
                                      .then(forms => setCustomerConsentForms(prev => ({ ...prev, [newId]: forms })))
                                      .catch(() => {});
                                  }
                                }}
                                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
                                title="Expand details"
                              >
                                {expandedCustomer === customer.customer_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => openCashPaymentModal(customer)}
                                className="p-2 text-slate-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                                title="Record cash payment"
                                data-testid={`cash-payment-btn-${customer.customer_id}`}
                              >
                                <DollarSign className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openPreorderModal(customer)}
                                className="p-2 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"
                                title="Create preorder"
                              >
                                <ShoppingBag className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openCustomerModal(customer)}
                                className="p-2 text-slate-400 hover:text-teal-600 rounded-lg hover:bg-teal-50"
                                title="Edit customer"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setPasswordResetCustomer(customer);
                                  setShowPasswordResetModal(true);
                                }}
                                className="p-2 text-slate-400 hover:text-purple-600 rounded-lg hover:bg-purple-50"
                                title="Reset password"
                              >
                                <Key className="h-4 w-4" />
                              </button>
                              {customer.status === 'banned' ? (
                                <button
                                  onClick={() => handleUnbanCustomer(customer)}
                                  className="p-2 text-red-500 hover:text-green-600 rounded-lg hover:bg-green-50"
                                  title="Unban this account"
                                  data-testid={`unban-btn-${customer.customer_id}`}
                                >
                                  <ShieldOff className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBanCustomer(customer)}
                                  className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                                  title="Ban / block this account"
                                  data-testid={`ban-btn-${customer.customer_id}`}
                                >
                                  <Ban className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={async () => {
                                  try {
                                    const r = await adminFetch(`${API_URL}/api/admin/customers/${customer.customer_id}`, { method: 'PUT', body: JSON.stringify({ is_wholesale: !customer.is_wholesale }) });
                                    if (!r.ok) throw new Error('Update failed');
                                    toast.success(customer.is_wholesale ? 'Wholesale access removed' : 'Wholesale access granted');
                                    fetchCustomers();
                                  } catch (e) { toast.error(e.message); }
                                }}
                                className={`p-2 rounded-lg ${customer.is_wholesale ? 'text-teal-600 hover:bg-teal-50' : 'text-slate-400 hover:text-teal-600 hover:bg-teal-50'}`}
                                title={customer.is_wholesale ? 'Wholesale access ON — click to remove' : 'Grant wholesale catalog access'}
                                data-testid={`wholesale-toggle-${customer.customer_id}`}
                              >
                                <Store className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCustomer(customer.customer_id)}
                                className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                                title="Delete customer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {expandedCustomer === customer.customer_id && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            {/* Research Consent Banner */}
                            <div className={`mb-4 p-3 rounded-lg flex items-center gap-3 ${customer.research_consent ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                              <Shield className={`h-5 w-5 ${customer.research_consent ? 'text-green-600' : 'text-amber-600'}`} />
                              <div>
                                <p className={`text-sm font-medium ${customer.research_consent ? 'text-green-800' : 'text-amber-800'}`}>
                                  {customer.research_consent ? 'Research Consent: Confirmed ✓' : 'Research Consent: Not yet provided'}
                                </p>
                                {customer.research_consent_date && (
                                  <p className="text-xs text-green-600">
                                    Agreed on {new Date(customer.research_consent_date).toLocaleDateString()} at {new Date(customer.research_consent_date).toLocaleTimeString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Phone</p>
                                <p className="text-sm text-slate-900">{customer.phone || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Last Order</p>
                                <p className="text-sm text-slate-900">
                                  {customer.last_order ? new Date(customer.last_order).toLocaleDateString() : 'Never'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Tags</p>
                                <div className="flex flex-wrap gap-1">
                                  {(customer.tags || []).length > 0 ? customer.tags.map((tag, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                                  )) : <span className="text-sm text-slate-400">No tags</span>}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Member Since</p>
                                <p className="text-sm text-slate-900">
                                  {new Date(customer.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            
                            {/* Store Credit Management */}
                            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4" data-testid={`credit-section-${customer.customer_id}`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-teal-600 font-medium">Store Credit Balance</p>
                                  <p className="text-lg font-bold text-teal-800">${(customer.store_credit || 0).toFixed(2)}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={async () => {
                                      const amount = prompt('Enter credit amount to ADD (e.g., 10.00):');
                                      if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) return;
                                      const reason = prompt('Reason for adding credit:') || 'Admin adjustment';
                                      try {
                                        const res = await adminFetch(`${API_URL}/api/admin/customers/${customer.customer_id}/adjust-credit`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ amount: parseFloat(amount), reason })
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.detail || 'Failed');
                                        toast.success(`$${parseFloat(amount).toFixed(2)} credit added — new balance: $${data.new_balance.toFixed(2)}`);
                                        fetchCustomers();
                                      } catch (e) { toast.error(e.message); }
                                    }}
                                    className="px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                                    data-testid={`add-credit-${customer.customer_id}`}
                                  >
                                    + Add Credit
                                  </button>
                                  <button
                                    onClick={async () => {
                                      const amount = prompt(`Enter credit amount to DEDUCT (max $${(customer.store_credit || 0).toFixed(2)}):`);
                                      if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) return;
                                      const reason = prompt('Reason for deducting credit:') || 'Admin adjustment';
                                      try {
                                        const res = await adminFetch(`${API_URL}/api/admin/customers/${customer.customer_id}/adjust-credit`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ amount: -parseFloat(amount), reason })
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.detail || 'Failed');
                                        toast.success(`$${parseFloat(amount).toFixed(2)} credit deducted — new balance: $${data.new_balance.toFixed(2)}`);
                                        fetchCustomers();
                                      } catch (e) { toast.error(e.message); }
                                    }}
                                    className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                                    data-testid={`deduct-credit-${customer.customer_id}`}
                                  >
                                    - Deduct
                                  </button>
                                </div>
                              </div>
                            </div>
                            {customer.notes && (
                              <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-xs text-slate-500 mb-1">Notes</p>
                                <p className="text-sm text-slate-700">{customer.notes}</p>
                              </div>
                            )}
                            {customer.address && customer.address.address_line1 && (
                              <div className="bg-slate-50 rounded-lg p-3 mt-3" data-testid={`customer-address-display-${customer.customer_id}`}>
                                <p className="text-xs text-slate-500 mb-1">Shipping Address</p>
                                <p className="text-sm text-slate-700">
                                  {customer.address.address_line1}
                                  {customer.address.address_line2 && <><br />{customer.address.address_line2}</>}
                                  <br />{customer.address.city}{customer.address.state && `, ${customer.address.state}`} {customer.address.zip_code}
                                  {customer.address.country && customer.address.country !== 'US' && <><br />{customer.address.country}</>}
                                </p>
                              </div>
                            )}
                            {/* Third-Party Consent Forms */}
                            {customer.allow_consent_send && (
                              <div className="mt-4" data-testid={`customer-consent-forms-${customer.customer_id}`}>
                                <p className="text-xs text-slate-500 mb-2">Third-Party Consent Forms Sent</p>
                                {(customerConsentForms[customer.customer_id] || []).length > 0 ? (
                                  <div className="space-y-2">
                                    {customerConsentForms[customer.customer_id].map((form) => (
                                      <div key={form.form_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-100">
                                        <div>
                                          <p className="text-sm font-medium text-slate-700">{form.recipient_name}</p>
                                          <p className="text-xs text-slate-400">{form.recipient_email}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {form.status === 'signed' ? (
                                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Signed</span>
                                          ) : (
                                            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">Pending</span>
                                          )}
                                          <span className="text-xs text-slate-400">{new Date(form.created_at).toLocaleDateString()}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-400 italic">No consent forms sent yet</p>
                                )}
                              </div>
                            )}
                            {/* Communication History */}
                            {(customer.communication_history || []).length > 0 && (
                              <div className="mt-4">
                                <p className="text-xs text-slate-500 mb-2">Recent Activity</p>
                                <div className="space-y-2">
                                  {customer.communication_history.slice(0, 3).map((note, i) => (
                                    <div key={i} className="flex items-start gap-2 text-sm">
                                      <MessageSquare className="h-4 w-4 text-slate-400 mt-0.5" />
                                      <div>
                                        <p className="text-slate-700">{note.content}</p>
                                        <p className="text-xs text-slate-400">{new Date(note.created_at).toLocaleString()}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {filteredCustomers.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No customers yet</p>
                    <Button onClick={() => openCustomerModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Your First Customer
                    </Button>
                  </div>
                )}
              </>

  );
};

export default CustomersListPanel;
